import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { type DeviceRoute, useDeviceRoutes } from '@/hooks/useDeviceRoutes';
import {
  type DeviceRouteChangeEvent,
  useDeviceRoutesHistory,
} from '@/hooks/useDeviceRoutesHistory';
import {
  datetimeLocalToMs,
  defaultHistoricMs,
  msToDatetimeLocal,
  msToUtcIso,
} from './deviceRoutesTime';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EmptyState } from '@/components/ui/empty-state';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface DeviceRoutesTabProps {
  deviceId: string | undefined;
}

type TimeMode = 'now' | 'historic';

type SortKey = keyof Pick<
  DeviceRoute,
  | 'destinationSubnet'
  | 'nextHopIp'
  | 'interfaceName'
  | 'routeType'
  | 'routeProtocol'
  | 'metric'
>;

interface SortState {
  key: SortKey;
  dir: 'asc' | 'desc';
}

function compareRoutes(a: DeviceRoute, b: DeviceRoute, sort: SortState): number {
  const av = a[sort.key];
  const bv = b[sort.key];
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  let cmp: number;
  if (typeof av === 'number' && typeof bv === 'number') {
    cmp = av - bv;
  } else {
    cmp = String(av).localeCompare(String(bv));
  }
  return sort.dir === 'asc' ? cmp : -cmp;
}

const HISTORY_PAGE_SIZE = 20;

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  onSort: (key: SortKey) => void;
}) {
  const active = sort.key === sortKey;
  return (
    <th
      className="text-left py-2 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer select-none hover:text-foreground"
      onClick={() => { onSort(sortKey); }}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          sort.dir === 'asc' ? (
            <ArrowUp className="w-3 h-3" />
          ) : (
            <ArrowDown className="w-3 h-3" />
          )
        ) : null}
      </span>
    </th>
  );
}

export default function DeviceRoutesTab({ deviceId }: DeviceRoutesTabProps) {
  const [timeMode, setTimeMode] = useState<TimeMode>('now');
  const [historicAtMs, setHistoricAtMs] = useState<number>(defaultHistoricMs());
  const [sort, setSort] = useState<SortState>({
    key: 'destinationSubnet',
    dir: 'asc',
  });
  const [historyEndMs, setHistoryEndMs] = useState<number | null>(null);

  const historicValue = useMemo(() => msToDatetimeLocal(historicAtMs), [historicAtMs]);
  const atMs = timeMode === 'historic' ? historicAtMs : null;

  const { data, isLoading, isError, isFetching } = useDeviceRoutes(deviceId, atMs);
  const {
    data: history,
    isLoading: historyLoading,
    isError: historyError,
  } = useDeviceRoutesHistory(deviceId, {
    endMs: historyEndMs,
    changes: HISTORY_PAGE_SIZE,
  });

  const sortedRoutes = useMemo(() => {
    if (!data) return [];
    return [...data.routes].sort((a, b) => compareRoutes(a, b, sort));
  }, [data, sort]);
  // Only devices that name VRFs get the column.
  const hasVrf = sortedRoutes.some((r) => r.vrf);

  const toggleSort = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    );
  };

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (isError || !data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load routing table.</AlertDescription>
      </Alert>
    );
  }

  const observedAt = new Date(data.observedAtMs);

  return (
    <section className="bg-card border border-border rounded-lg p-5">
      {/* Time-control row */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <RadioGroup
          value={timeMode}
          onValueChange={(v) => { setTimeMode(v as TimeMode); }}
          className="flex items-center gap-3"
        >
          <Label className="inline-flex items-center gap-1.5 cursor-pointer text-sm">
            <RadioGroupItem value="now" />
            Now
          </Label>
          <Label className="inline-flex items-center gap-1.5 cursor-pointer text-sm">
            <RadioGroupItem value="historic" />
            Historic
          </Label>
        </RadioGroup>
        {timeMode === 'historic' ? (
          <>
            <Input
              type="datetime-local"
              value={historicValue}
              onChange={(e) => {
                const ms = datetimeLocalToMs(e.target.value);
                if (ms != null) setHistoricAtMs(ms);
              }}
              className="w-auto"
            />
            {atMs != null ? (
              <span
                className="text-xs text-muted-foreground"
                title="UTC equivalent of the selected local time (this is what's sent to the API)"
              >
                = <span className="font-mono">{msToUtcIso(atMs)}</span>
              </span>
            ) : null}
          </>
        ) : null}
        <div className="ml-auto text-xs text-muted-foreground" title={`UTC: ${observedAt.toISOString()}`}>
          {isFetching
            ? 'Refreshing…'
            : `Observed at: ${observedAt.toLocaleString()} (local)`}
        </div>
      </div>

      {sortedRoutes.length === 0 ? (
        <EmptyState
          variant="no-data"
          title={
            timeMode === 'historic'
              ? 'No routing data at the selected time'
              : 'No routing data yet'
          }
          size="inline"
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border-subtle">
              <tr>
                {hasVrf && <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">VRF</th>}
                <SortHeader label="Destination" sortKey="destinationSubnet" sort={sort} onSort={toggleSort} />
                <SortHeader label="Next hop" sortKey="nextHopIp" sort={sort} onSort={toggleSort} />
                <SortHeader label="Interface" sortKey="interfaceName" sort={sort} onSort={toggleSort} />
                <SortHeader label="Protocol" sortKey="routeProtocol" sort={sort} onSort={toggleSort} />
                <SortHeader label="Type" sortKey="routeType" sort={sort} onSort={toggleSort} />
                <SortHeader label="Metric" sortKey="metric" sort={sort} onSort={toggleSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {sortedRoutes.map((r) => (
                <tr
                  // The VRF is part of a route's identity: arista holds
                  // 192.168.0.0/24 in both `default` and `core`, and without
                  // it the two rows shared a key.
                  key={`${r.vrf ?? ''}|${r.destinationSubnet}|${r.nextHopIp ?? ''}|${r.interfaceName ?? r.interfaceIfindex ?? ''}`}
                  className="hover:bg-bg-subtle"
                >
                  {hasVrf && <td className="py-2 px-3 font-mono text-xs">{r.vrf ?? <span className="text-muted-foreground">—</span>}</td>}
                  <td className="py-2 px-3"><code className="font-mono text-xs">{r.destinationSubnet}</code></td>
                  <td className="py-2 px-3">
                    {r.nextHopIp ? (
                      <code className="font-mono text-xs">{r.nextHopIp}</code>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    {r.interfaceName ?? (
                      r.interfaceIfindex ? (
                        <span className="font-mono text-xs text-muted-foreground">
                          ifIndex {r.interfaceIfindex}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )
                    )}
                  </td>
                  <td className="py-2 px-3">
                    {r.routeProtocol ?? <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="py-2 px-3">
                    {r.routeType ?? <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="py-2 px-3">
                    {r.metric ?? <span className="text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ChangeHistory
        events={history?.events ?? []}
        loading={historyLoading}
        error={historyError}
        hasMore={history?.hasMore ?? false}
        oldestMs={history?.oldestTimestampMs ?? null}
        onLoadOlder={(ms) => { setHistoryEndMs(ms); }}
        onJumpTo={(ms) => {
          setHistoricAtMs(ms);
          setTimeMode('historic');
        }}
      />
    </section>
  );
}

interface ChangeHistoryProps {
  events: DeviceRouteChangeEvent[];
  loading: boolean;
  error: boolean;
  hasMore: boolean;
  oldestMs: number | null;
  onLoadOlder: (endMs: number) => void;
  onJumpTo: (ms: number) => void;
}

function ChangeHistory({
  events,
  loading,
  error,
  hasMore,
  oldestMs,
  onLoadOlder,
  onJumpTo,
}: ChangeHistoryProps) {
  if (loading && events.length === 0) {
    return (
      <div className="mt-6">
        <h3 className="text-sm font-semibold mb-3">Change history</h3>
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="mt-6">
        <h3 className="text-sm font-semibold mb-3">Change history</h3>
        <Alert>
          <AlertDescription>
            Failed to load change history. The current state above is still accurate.
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  if (events.length === 0) {
    return (
      <div className="mt-6">
        <h3 className="text-sm font-semibold mb-3">Change history</h3>
        <p className="text-sm text-muted-foreground">
          No change events recorded yet for this device.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <h3 className="text-sm font-semibold">Change history</h3>
      <p className="text-xs text-muted-foreground mt-1 mb-3">
        Newest first. Click any event to view the routing table state at that moment.
      </p>
      <div className="space-y-2">
        {events.map((ev) => (
          <ChangeRow
            key={ev.timestampMs}
            event={ev}
            onClick={() => { onJumpTo(ev.timestampMs); }}
          />
        ))}
      </div>
      <div className="mt-3 text-center">
        {hasMore && oldestMs != null ? (
          <Button variant="outline" size="sm" onClick={() => { onLoadOlder(oldestMs); }}>
            Load older changes
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">
            No older changes in the last 30 days.
          </span>
        )}
      </div>
    </div>
  );
}

function ChangeRow({
  event,
  onClick,
}: {
  event: DeviceRouteChangeEvent;
  onClick: () => void;
}) {
  const ts = new Date(event.timestampMs);
  const isSnapshot = event.kind === 'snapshot';
  return (
    <div
      className="rounded-md border border-border bg-bg-subtle p-3 cursor-pointer hover:border-foreground/30 transition-colors"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      title={`UTC: ${ts.toISOString()}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Badge
          className={cn(
            'border-0 font-normal',
            isSnapshot
              ? 'bg-sev-5/15 text-sev-5'
              : 'bg-sev-3/20 text-foreground',
          )}
        >
          {isSnapshot ? 'Snapshot' : 'Delta'}
        </Badge>
        <span className="text-sm font-medium">{ts.toLocaleString()}</span>
        <span className="text-xs text-muted-foreground">
          ({event.state.length} {event.state.length === 1 ? 'route' : 'routes'} after)
        </span>
      </div>
      {!isSnapshot && (event.adds.length > 0 || event.removes.length > 0) ? (
        <div className="mt-2 space-y-0.5 text-xs">
          {event.adds.map((r) => (
            <div
              key={`+${r.vrf ?? ''}|${r.destinationSubnet}|${r.nextHopIp ?? ''}|${r.interfaceName ?? r.interfaceIfindex ?? ''}`}
            >
              <span className="text-sev-4">+ </span>
              {/* The VRF, when the device names one: the same prefix in two
                  VRFs is two routes, and the history must say which changed. */}
              {r.vrf && <code className="font-mono text-muted-foreground">[{r.vrf}] </code>}
              <code className="font-mono">{r.destinationSubnet}</code>
              {r.nextHopIp ? (
                <>
                  {' via '}
                  <code className="font-mono">{r.nextHopIp}</code>
                </>
              ) : null}
              {r.interfaceName ? (
                <>
                  {' on '}
                  <code className="font-mono">{r.interfaceName}</code>
                </>
              ) : null}
            </div>
          ))}
          {event.removes.map((r) => (
            <div
              key={`-${r.vrf ?? ''}|${r.destinationSubnet}|${r.nextHopIp ?? ''}|${r.interfaceName ?? r.interfaceIfindex ?? ''}`}
            >
              <span className="text-sev-1">− </span>
              {/* The VRF, when the device names one: the same prefix in two
                  VRFs is two routes, and the history must say which changed. */}
              {r.vrf && <code className="font-mono text-muted-foreground">[{r.vrf}] </code>}
              <code className="font-mono">{r.destinationSubnet}</code>
              {r.nextHopIp ? (
                <>
                  {' via '}
                  <code className="font-mono">{r.nextHopIp}</code>
                </>
              ) : null}
              {r.interfaceName ? (
                <>
                  {' on '}
                  <code className="font-mono">{r.interfaceName}</code>
                </>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
